import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './VerifyCallerIdModal.css';

const VERIFY_ENDPOINT = 'https://us-central1-maia-tech.cloudfunctions.net/verifyCallerId';
const REQUEST_MANUAL_ENDPOINT = 'https://us-central1-maia-tech.cloudfunctions.net/requestManualCallerIdVerification';

const POLL_INTERVAL_MS = 3000;
const POLL_WINDOW_MS = 90000;
const MAX_RETRIES = 3;

const formatDisplay = (digits) => {
  if (!digits) return '';
  const local = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (local.length <= 3) return local;
  if (local.length <= 6) return `(${local.slice(0, 3)}) ${local.slice(3)}`;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`;
};

const toE164 = (raw) => {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d[0] === '1') return `+${d}`;
  return null;
};

const maskE164 = (e164) => {
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return e164;
  return `(***) ***-${digits.slice(-4)}`;
};

const VerifyCallerIdModal = ({ isOpen, onClose, currentUser, onSuccess, initialEmail = '', initialPhone = '' }) => {
  const initialPhoneDigits = (initialPhone || '').replace(/\D/g, '').slice(0, 11);
  const [view, setView] = useState('enter-phone');
  const [phoneInput, setPhoneInput] = useState(initialPhoneDigits);
  const [e164, setE164] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [tier2Email, setTier2Email] = useState(initialEmail);
  const [otpInput, setOtpInput] = useState('');

  const pollIntervalRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const closeBtnRef = useRef(null);
  const firstFocusRef = useRef(null);
  const containerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const resetAll = useCallback(() => {
    stopPolling();
    setView('enter-phone');
    setPhoneInput('');
    setE164('');
    setValidationCode('');
    setCountdown(0);
    setRetryCount(0);
    setErrorMessage('');
    setIsBusy(false);
    setOtpInput('');
  }, [stopPolling]);

  useEffect(() => {
    if (!isOpen) return;
    setTier2Email((prev) => prev || initialEmail || '');
    setPhoneInput((prev) => prev || (initialPhone || '').replace(/\D/g, '').slice(0, 11));
    const t = setTimeout(() => firstFocusRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen, initialEmail, initialPhone]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (view !== 'enter-code') return undefined;
    if (countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [view, countdown]);

  const handlePhoneChange = (raw) => {
    const digitsOnly = raw.replace(/\D/g, '').slice(0, 11);
    setPhoneInput(digitsOnly);
    setErrorMessage('');
  };

  const startVerification = useCallback(async (attempt) => {
    const e164Number = toE164(phoneInput);
    if (!e164Number) {
      setErrorMessage('Please enter a 10-digit US phone number.');
      return;
    }
    setIsBusy(true);
    setErrorMessage('');
    try {
      const { data } = await axios.post(VERIFY_ENDPOINT, {
        action: 'start',
        uid: currentUser.uid,
        phone_number: e164Number,
        retry_count: attempt,
      });
      if (data.status === 'already_verified') {
        setE164(e164Number);
        setView('already-yours');
        onSuccess?.(e164Number);
        return;
      }
      if (data.status === 'otp_sent') {
        setE164(e164Number);
        setOtpInput('');
        setView('enter-otp');
        return;
      }
      if (data.status === 'verification_started') {
        setE164(e164Number);
        setValidationCode(String(data.validation_code || ''));
        setCountdown(Math.floor(POLL_WINDOW_MS / 1000));
        setView('enter-code');
        pollDeadlineRef.current = Date.now() + POLL_WINDOW_MS;
        stopPolling();
        pollIntervalRef.current = setInterval(async () => {
          try {
            const res = await axios.post(VERIFY_ENDPOINT, {
              action: 'check',
              uid: currentUser.uid,
              phone_number: e164Number,
            });
            if (res.data?.verified) {
              stopPolling();
              setView('success');
              onSuccess?.(e164Number);
              return;
            }
          } catch (_err) {
            /* transient errors during polling are non-fatal */
          }
          if (Date.now() > pollDeadlineRef.current) {
            stopPolling();
            setView(attempt >= MAX_RETRIES - 1 ? 'tier2' : 'retry');
          }
        }, POLL_INTERVAL_MS);
      } else {
        setErrorMessage('Verification could not start. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || 'Failed to start verification. Please try again.');
    } finally {
      setIsBusy(false);
    }
  }, [phoneInput, currentUser, onSuccess, stopPolling]);

  const handleCallMe = () => startVerification(0);
  const handleRetry = () => {
    const next = retryCount + 1;
    setRetryCount(next);
    startVerification(next);
  };

  const handleTier2Submit = async () => {
    const e164Number = e164 || toE164(phoneInput);
    if (!e164Number) {
      setErrorMessage('Please enter a 10-digit US phone number.');
      return;
    }
    if (!tier2Email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(tier2Email)) {
      setErrorMessage('Please provide a valid email address so we can notify you.');
      return;
    }
    setIsBusy(true);
    setErrorMessage('');
    try {
      await axios.post(REQUEST_MANUAL_ENDPOINT, {
        uid: currentUser.uid,
        phone_number: e164Number,
        email: tier2Email,
      });
      setView('tier2-submitted');
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || 'Could not submit request. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleOtpSubmit = async () => {
    const code = otpInput.replace(/\D/g, '');
    if (code.length < 4) {
      setErrorMessage('Enter the 6-digit code from your text message.');
      return;
    }
    setIsBusy(true);
    setErrorMessage('');
    try {
      const { data } = await axios.post(VERIFY_ENDPOINT, {
        action: 'verify_otp',
        uid: currentUser.uid,
        phone_number: e164,
        code,
      });
      if (data?.verified) {
        setView('success');
        onSuccess?.(e164);
      } else {
        setErrorMessage(data?.message || "Code didn't match. Try again or resend.");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.response?.data?.error || "Code didn't match. Try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleOtpResend = async () => {
    setIsBusy(true);
    setErrorMessage('');
    try {
      const { data } = await axios.post(VERIFY_ENDPOINT, {
        action: 'start',
        uid: currentUser.uid,
        phone_number: e164,
      });
      if (data.status !== 'otp_sent') {
        setErrorMessage('Could not resend the code. Please close and try again.');
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || 'Could not resend the code.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    onClose?.();
  };

  const countdownLabel = useMemo(() => {
    const m = Math.floor(countdown / 60);
    const s = countdown % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, [countdown]);

  if (!isOpen) return null;

  const isSuccessView = view === 'success' || view === 'already-yours';

  const headerTitle =
    view === 'already-yours' ? 'Your number is already verified'
    : view === 'success' ? 'Verified'
    : 'Verify your phone number';

  return (
    <div
      className="vcim-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vcim-title"
      onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
      ref={containerRef}
    >
      <div className="vcim-modal">
        <div className={`vcim-header${isSuccessView ? ' vcim-header-centered' : ''}`}>
          <h2 id="vcim-title">{headerTitle}</h2>
          <button
            type="button"
            className="vcim-close"
            aria-label="Close verification"
            onClick={handleClose}
            ref={closeBtnRef}
          >
            &times;
          </button>
        </div>

        <div className="vcim-body">
          {view === 'enter-phone' && (
            <>
              <p>To keep callbacks reaching you directly, Maia needs to verify your phone number. We'll send you a 6-digit code — usually by text, sometimes by phone call. This takes about a minute.</p>
              <label htmlFor="vcim-phone" className="vcim-label">Phone number</label>
              <input
                id="vcim-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className="vcim-input"
                placeholder="(XXX) XXX-XXXX"
                value={formatDisplay(phoneInput)}
                onChange={(e) => handlePhoneChange(e.target.value)}
                ref={firstFocusRef}
              />
              {errorMessage && <p className="vcim-error" role="alert">{errorMessage}</p>}
              <button
                type="button"
                className="vcim-primary"
                disabled={isBusy || !toE164(phoneInput)}
                onClick={handleCallMe}
              >
                {isBusy ? 'Starting…' : 'Send code'}
              </button>
            </>
          )}

          {view === 'enter-otp' && (
            <>
              <p>We sent a 6-digit code by text message to <strong>{formatDisplay(phoneInput)}</strong>. Enter it below to finish verifying.</p>
              <label htmlFor="vcim-otp" className="vcim-label">Verification code</label>
              <input
                id="vcim-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="vcim-input"
                placeholder="123456"
                value={otpInput}
                maxLength={10}
                onChange={(e) => { setOtpInput(e.target.value); setErrorMessage(''); }}
                ref={firstFocusRef}
              />
              {errorMessage && <p className="vcim-error" role="alert">{errorMessage}</p>}
              <button
                type="button"
                className="vcim-primary"
                disabled={isBusy || otpInput.replace(/\D/g, '').length < 4}
                onClick={handleOtpSubmit}
              >
                {isBusy ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className="vcim-link"
                disabled={isBusy}
                onClick={handleOtpResend}
              >
                Didn't get a text? Resend
              </button>
            </>
          )}

          {view === 'enter-code' && (
            <>
              <p>Twilio is calling <strong>{formatDisplay(phoneInput)}</strong> now. Answer the phone and enter this code when prompted:</p>
              <div
                className="vcim-code"
                aria-live="polite"
                aria-label={`Verification code: ${validationCode.split('').join(' ')}`}
              >
                {validationCode}
              </div>
              <p className="vcim-meta">Waiting for verification… <span aria-hidden="true">{countdownLabel}</span></p>
              <p className="vcim-hint">Only you should see this code. Don't share your screen during this step.</p>
              <button
                type="button"
                className="vcim-link"
                onClick={() => { stopPolling(); setView('retry'); }}
              >
                Didn't get a call?
              </button>
            </>
          )}

          {view === 'retry' && (
            <>
              <p>The call didn't reach you within 90 seconds. Some carriers drop Twilio verification calls — trying again usually works.</p>
              <p className="vcim-meta">Attempt {retryCount + 1} of {MAX_RETRIES}.</p>
              {errorMessage && <p className="vcim-error" role="alert">{errorMessage}</p>}
              <button
                type="button"
                className="vcim-primary"
                disabled={isBusy}
                onClick={handleRetry}
                ref={firstFocusRef}
              >
                {isBusy ? 'Calling…' : 'Call again'}
              </button>
              <button
                type="button"
                className="vcim-link"
                onClick={() => setView('tier2')}
              >
                Still having trouble? Verify manually
              </button>
            </>
          )}

          {view === 'tier2' && (
            <>
              <p>Some mobile carriers don't reliably deliver Twilio's verification call. We can verify your number manually within one business day.</p>
              <label htmlFor="vcim-tier2-phone" className="vcim-label">Phone number</label>
              <input
                id="vcim-tier2-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className="vcim-input"
                value={formatDisplay(phoneInput)}
                onChange={(e) => handlePhoneChange(e.target.value)}
                ref={firstFocusRef}
              />
              <label htmlFor="vcim-tier2-email" className="vcim-label">Email (so we can notify you)</label>
              <input
                id="vcim-tier2-email"
                type="email"
                autoComplete="email"
                className="vcim-input"
                value={tier2Email}
                onChange={(e) => setTier2Email(e.target.value)}
              />
              {errorMessage && <p className="vcim-error" role="alert">{errorMessage}</p>}
              <button
                type="button"
                className="vcim-primary"
                disabled={isBusy}
                onClick={handleTier2Submit}
              >
                {isBusy ? 'Submitting…' : 'Submit request'}
              </button>
            </>
          )}

          {view === 'tier2-submitted' && (
            <>
              <h3>Request received</h3>
              <p>We'll email you at <strong>{tier2Email}</strong> once your number is verified, usually within one business day.</p>
              <button type="button" className="vcim-primary" onClick={handleClose} ref={firstFocusRef}>
                Close
              </button>
            </>
          )}

          {view === 'already-yours' && (
            <>
              <p>Calls from Maia will show <strong>{maskE164(e164)}</strong> so callbacks reach you directly.</p>
              <button
                type="button"
                className="vcim-primary"
                onClick={() => { resetAll(); handleClose(); }}
                ref={firstFocusRef}
              >
                Done
              </button>
            </>
          )}

          {view === 'success' && (
            <>
              <p>Calls from Maia will show <strong>{maskE164(e164)}</strong> so callbacks reach you directly.</p>
              <button
                type="button"
                className="vcim-primary"
                onClick={() => { resetAll(); handleClose(); }}
                ref={firstFocusRef}
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyCallerIdModal;
